import {
  foreignBenefitFrequencyOptions,
  ONE_TIME,
  FUTURE,
  CONTINUING,
  OTHER,
} from 'constants/enums/foreignActivityOptions';
import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST, LAST_SEVEN_YEARS } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import {
  DATE_IN_THE_PAST,
  END_DATE_TOO_EARLY,
  NOT_WITHIN_LAST_SEVEN_YEARS,
  OLDEST_SELECTED_BIRTHDATE,
} from 'constants/errors';
import currencyModelValidator from 'forms/shared/validators/models/currency';
import { SafeDateTime } from 'helpers/types';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { ForeignActivitiesBenefitTypeItemModel } from '../types';

export const foreignActivitiesBenefitTypeItemValidator: ValidateJSModel<ForeignActivitiesBenefitTypeItemModel> = {
  Country: (value, attributes) => {
    if (attributes.CountryFocused) {
      return {};
    }

    return { hasValue: true, country: { excludeUnitedStates: true } };
  },

  Value: { presence: true, model: { validator: currencyModelValidator } },

  ValueEstimated: {},

  Reason: { hasValue: true },

  Obligated: { hasValue: { validator: hasYesOrNo } },

  ObligatedExplanation: (value, attributes) => {
    if (attributes.Obligated?.value === 'Yes') { return { hasValue: true }; }

    return {};
  },

  Received: (value, attributes, attributeName, options) => {
    if (options.benefitType === ONE_TIME) {
      const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options.applicantBirthdate)?.datetime?.plus({ years: 18 })));
      let earliestDate = getSafeDateTime(LAST_SEVEN_YEARS);
      let earlyError = NOT_WITHIN_LAST_SEVEN_YEARS;

      if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid
      && isSafeDateLater(earliestDate, applicantEighteenthBirthdate)) {
        earliestDate = applicantEighteenthBirthdate;
        earlyError = 'error.beforeDuration';
      }

      return {
        presence: true,
        date: {
          earliest: earliestDate.datetime,
          tooEarly: earlyError,
          latest: DEFAULT_LATEST,
        },
      };
    }

    return {};
  },

  Began: (value, attributes, attributeName, options) => {
    const { benefitType } = options;

    if ([FUTURE, CONTINUING, OTHER].includes(benefitType)) {
      if ([CONTINUING, OTHER].includes(benefitType)) {
        return {
          presence: true,
          date: {
            earliest: options.relationshipsOldestBirthdate,
            tooEarly: OLDEST_SELECTED_BIRTHDATE,
            latest: DEFAULT_LATEST,
          },
        };
      }

      return {
        presence: true,
        date: {
          earliest: DEFAULT_LATEST,
          tooEarly: DATE_IN_THE_PAST,
        },
      };
    }

    return {};
  },

  Frequency: (value, attributes, attributeName, options) => {
    const { benefitType } = options;

    if ([FUTURE, CONTINUING, OTHER].includes(benefitType)) {
      return {
        hasValue: { validator: { inclusion: foreignBenefitFrequencyOptions } },
      };
    }

    return {};
  },

  OtherFrequency: (value, attributes) => {
    if (attributes.Frequency?.value === 'Other') { return { hasValue: true }; }

    return {};
  },

  End: (value, attributes, attributeName, options) => {
    if ([CONTINUING, OTHER].includes(options.benefitType) && attributes.Began) {
      return {
        presence: true,
        date: {
          earliest: attributes.Began,
          tooEarly: END_DATE_TOO_EARLY,
        },
      };
    }

    return {};
  },

  OtherFrequencyTypeExplanation: (value, attributes, attributeName, options) => {
    if (options.benefitType === OTHER) { return { hasValue: true }; }

    return {};
  },
};
